import type {
  AttackFinger,
  RhythmFingerOrRest,
} from "../../lib/rhythm/rhythmPathPlan";
import { ATTACK_FINGER_LABEL } from "../../lib/rhythm/rhythmPathPlan";

/** Solo p · i · m (sin anular en rhythm-basics). */
const FINGER_ORDER: AttackFinger[] = ["p", "i", "m"];

export interface FingerIndicatorProps {
  /** Dedo activo, o "rest" para silencio (shh). */
  active: RhythmFingerOrRest | null;
  size?: "sm" | "lg";
  className?: string;
}

/**
 * Indicador visual de dedo de ataque (p / i / m).
 * La cuerda se señala con círculos numerados (no texto).
 */
export function FingerIndicator({
  active,
  size = "lg",
  className = "",
}: FingerIndicatorProps) {
  const isLg = size === "lg";
  const isRest = active === "rest";
  const activeFinger = !isRest && active != null ? active : null;

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <div
        className={`flex items-end justify-center gap-2 sm:gap-3 ${
          isLg ? "min-h-[6.5rem]" : "min-h-[3.25rem]"
        }`}
        role="group"
        aria-label={
          isRest
            ? "Silencio: no ataques"
            : activeFinger
              ? `Dedo activo: ${ATTACK_FINGER_LABEL[activeFinger]}`
              : "Indicador de dedos de ataque"
        }
      >
        {FINGER_ORDER.map((finger) => {
          const on = activeFinger === finger;
          return (
            <div key={finger} className="flex flex-col items-center gap-1.5">
              <span
                className={`transition-all duration-200 ${
                  isLg
                    ? on
                      ? "text-[11px] font-medium tracking-wide text-[#99f6e4]"
                      : "text-[10px] text-white/25"
                    : on
                      ? "text-[9px] text-[#99f6e4]/90"
                      : "text-[8px] text-white/20"
                }`}
              >
                {ATTACK_FINGER_LABEL[finger]}
              </span>
              <div
                className={`flex items-center justify-center rounded-2xl border font-serif transition-all duration-200 ${
                  isLg
                    ? on
                      ? "h-16 w-16 scale-110 border-[#99f6e4]/70 bg-[#99f6e4]/20 text-3xl text-[#99f6e4] shadow-[0_0_28px_rgba(153,246,228,0.25)]"
                      : isRest
                        ? "h-14 w-14 border-white/5 bg-white/[0.02] text-2xl text-white/15"
                        : "h-14 w-14 border-white/10 bg-white/[0.03] text-2xl text-white/30"
                    : on
                      ? "h-9 w-9 border-[#99f6e4]/60 bg-[#99f6e4]/15 text-base text-[#99f6e4]"
                      : "h-8 w-8 border-white/10 bg-white/[0.03] text-sm text-white/25"
                }`}
                aria-current={on ? "true" : undefined}
              >
                {finger}
              </div>
            </div>
          );
        })}
      </div>

      {isLg && (
        <p className="text-center text-sm text-white/70 min-h-[1.25rem]">
          {isRest ? (
            <span className="font-medium tracking-wide text-amber-200/90">
              shh · silencio
            </span>
          ) : activeFinger ? (
            <>
              Ataca con{" "}
              <span className="font-medium text-[#99f6e4]">
                {ATTACK_FINGER_LABEL[activeFinger]}
              </span>
            </>
          ) : (
            <span className="text-white/35">Espera el golpe…</span>
          )}
        </p>
      )}
    </div>
  );
}
